/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface CommunicationCenterProps {
  id: string;
  name: string;
  timezoneCd: string | null;
  primaryPhoneNumber: string | null;
  emailAddress: string | null;
  states: List<string> | null;
  includeCompetitors: boolean;
  showReferToGmrWarning: boolean | null;
  playAlertForManualPositionReports: boolean;
  enableRAAlert: boolean;
  useCrewPhoneNumber: boolean;
  alertFrequencyForGroundVehicles: number | null;
  displayContactTimeline: boolean;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class CommunicationCenterRecord extends Record<CommunicationCenterProps>({
  id: null as any,
  name: null as any,
  timezoneCd: null as any,
  primaryPhoneNumber: null as any,
  emailAddress: null as any,
  states: null as any,
  includeCompetitors: null as any,
  showReferToGmrWarning: null as any,
  playAlertForManualPositionReports: null as any,
  enableRAAlert: null as any,
  useCrewPhoneNumber: null as any,
  alertFrequencyForGroundVehicles: null as any,
  displayContactTimeline: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    timezoneCd: string | null;
    primaryPhoneNumber: string | null;
    emailAddress: string | null;
    states: any;
    includeCompetitors: boolean;
    showReferToGmrWarning: boolean | null;
    playAlertForManualPositionReports: boolean;
    enableRAAlert: boolean;
    useCrewPhoneNumber: boolean;
    alertFrequencyForGroundVehicles: number | null;
    displayContactTimeline: boolean;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): CommunicationCenterRecord {
    if (json == null) {
      throw new Error('CommunicationCenterRecord.fromJS: parameter cannot be null');
    }
    return new CommunicationCenterRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'CommunicationCenterJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'CommunicationCenterJson.name'),
      timezoneCd: json.timezoneCd,
      primaryPhoneNumber: json.primaryPhoneNumber,
      emailAddress: json.emailAddress,
      states: json.states != null ? List<string>(json.states.map((o: any) => new String(o))) : null,
      includeCompetitors: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'includeCompetitors', false),
        'CommunicationCenterJson.includeCompetitors',
      ),
      showReferToGmrWarning: json.showReferToGmrWarning,
      playAlertForManualPositionReports: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'playAlertForManualPositionReports', false),
        'CommunicationCenterJson.playAlertForManualPositionReports',
      ),
      enableRAAlert: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'enableRAAlert', false),
        'CommunicationCenterJson.enableRAAlert',
      ),
      useCrewPhoneNumber: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'useCrewPhoneNumber', false),
        'CommunicationCenterJson.useCrewPhoneNumber',
      ),
      alertFrequencyForGroundVehicles: json.alertFrequencyForGroundVehicles,
      displayContactTimeline: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'displayContactTimeline', false),
        'CommunicationCenterJson.displayContactTimeline',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'CommunicationCenterJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    timezoneCd: string | null;
    primaryPhoneNumber: string | null;
    emailAddress: string | null;
    states: any;
    includeCompetitors: boolean;
    showReferToGmrWarning: boolean | null;
    playAlertForManualPositionReports: boolean;
    enableRAAlert: boolean;
    useCrewPhoneNumber: boolean;
    alertFrequencyForGroundVehicles: number | null;
    displayContactTimeline: boolean;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [CommunicationCenterRecord, Error[]] {
    if (json == null) {
      throw new Error('CommunicationCenterRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new CommunicationCenterRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'CommunicationCenterJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'CommunicationCenterJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      timezoneCd: tryOrDefault(
        () => json.timezoneCd,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      primaryPhoneNumber: tryOrDefault(
        () => json.primaryPhoneNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      emailAddress: tryOrDefault(
        () => json.emailAddress,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      states: tryOrDefault(
        () =>
          json.states != null ? List<string>(json.states.map((o: any) => new String(o))) : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      includeCompetitors: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'includeCompetitors', false),
            'CommunicationCenterJson.includeCompetitors',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      showReferToGmrWarning: tryOrDefault(
        () => json.showReferToGmrWarning,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      playAlertForManualPositionReports: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'playAlertForManualPositionReports', false),
            'CommunicationCenterJson.playAlertForManualPositionReports',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      enableRAAlert: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'enableRAAlert', false),
            'CommunicationCenterJson.enableRAAlert',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      useCrewPhoneNumber: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'useCrewPhoneNumber', false),
            'CommunicationCenterJson.useCrewPhoneNumber',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      alertFrequencyForGroundVehicles: tryOrDefault(
        () => json.alertFrequencyForGroundVehicles,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      displayContactTimeline: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'displayContactTimeline', false),
            'CommunicationCenterJson.displayContactTimeline',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'CommunicationCenterJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: CommunicationCenterProps) {
    super(props);
    if (props == null) {
      throw new Error('CommunicationCenterRecord constructor parameter cannot be null');
    }
  }
}

export abstract class CommunicationCenterViewModelBase implements CommunicationCenterProps {
  protected readonly _props: CommunicationCenterProps;
  private _json?: CommunicationCenterProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get timezoneCd(): string | null {
    return this._props.timezoneCd;
  }
  get primaryPhoneNumber(): string | null {
    return this._props.primaryPhoneNumber;
  }
  get emailAddress(): string | null {
    return this._props.emailAddress;
  }
  get states(): List<string> | null {
    return this._props.states;
  }
  get includeCompetitors(): boolean {
    return this._props.includeCompetitors;
  }
  get showReferToGmrWarning(): boolean | null {
    return this._props.showReferToGmrWarning;
  }
  get playAlertForManualPositionReports(): boolean {
    return this._props.playAlertForManualPositionReports;
  }
  get enableRAAlert(): boolean {
    return this._props.enableRAAlert;
  }
  get useCrewPhoneNumber(): boolean {
    return this._props.useCrewPhoneNumber;
  }
  get alertFrequencyForGroundVehicles(): number | null {
    return this._props.alertFrequencyForGroundVehicles;
  }
  get displayContactTimeline(): boolean {
    return this._props.displayContactTimeline;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: CommunicationCenterProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        timezoneCd: this._props.timezoneCd,
        primaryPhoneNumber: this._props.primaryPhoneNumber,
        emailAddress: this._props.emailAddress,
        states: this._props.states,
        includeCompetitors: this._props.includeCompetitors,
        showReferToGmrWarning: this._props.showReferToGmrWarning,
        playAlertForManualPositionReports: this._props.playAlertForManualPositionReports,
        enableRAAlert: this._props.enableRAAlert,
        useCrewPhoneNumber: this._props.useCrewPhoneNumber,
        alertFrequencyForGroundVehicles: this._props.alertFrequencyForGroundVehicles,
        displayContactTimeline: this._props.displayContactTimeline,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllCommunicationCenterEntities = (state: AppState) =>
  state.entityStores.communicationCenter.entities;

export const selectCommunicationCenterById = (id: string) => (state: AppState) =>
  state.entityStores.communicationCenter.entities.get(id);

export const selectCommunicationCenterMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.communicationCenter.meta.get(id);

export const selectCommunicationCenterAndMetadataById = (id: string) =>
  createSelector(
    selectCommunicationCenterById(id),
    selectCommunicationCenterMetadataById(id),
    (communicationCenter, meta) => ({ communicationCenter, meta }),
  );

export function selectCommunicationCenterEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllCommunicationCenterEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, CommunicationCenterRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectCommunicationCenterEntitiesByName(name: string) {
  return createSelector(selectAllCommunicationCenterEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectCommunicationCenterEntitiesByTimezoneCd(timezoneCd: string) {
  return createSelector(selectAllCommunicationCenterEntities, (entities) =>
    entities.filter((e) => e.timezoneCd === timezoneCd).valueSeq(),
  );
}

export function selectCommunicationCenterEntitiesByPrimaryPhoneNumber(primaryPhoneNumber: string) {
  return createSelector(selectAllCommunicationCenterEntities, (entities) =>
    entities.filter((e) => e.primaryPhoneNumber === primaryPhoneNumber).valueSeq(),
  );
}

export function selectCommunicationCenterEntitiesByEmailAddress(emailAddress: string) {
  return createSelector(selectAllCommunicationCenterEntities, (entities) =>
    entities.filter((e) => e.emailAddress === emailAddress).valueSeq(),
  );
}

export function selectCommunicationCenterEntitiesByIncludeCompetitors(includeCompetitors: boolean) {
  return createSelector(selectAllCommunicationCenterEntities, (entities) =>
    entities.filter((e) => e.includeCompetitors === includeCompetitors).valueSeq(),
  );
}

export function selectCommunicationCenterEntitiesByShowReferToGmrWarning(
  showReferToGmrWarning: boolean,
) {
  return createSelector(selectAllCommunicationCenterEntities, (entities) =>
    entities.filter((e) => e.showReferToGmrWarning === showReferToGmrWarning).valueSeq(),
  );
}

export function selectCommunicationCenterEntitiesByPlayAlertForManualPositionReports(
  playAlertForManualPositionReports: boolean,
) {
  return createSelector(selectAllCommunicationCenterEntities, (entities) =>
    entities
      .filter((e) => e.playAlertForManualPositionReports === playAlertForManualPositionReports)
      .valueSeq(),
  );
}

export function selectCommunicationCenterEntitiesByEnableRAAlert(enableRAAlert: boolean) {
  return createSelector(selectAllCommunicationCenterEntities, (entities) =>
    entities.filter((e) => e.enableRAAlert === enableRAAlert).valueSeq(),
  );
}

export function selectCommunicationCenterEntitiesByUseCrewPhoneNumber(useCrewPhoneNumber: boolean) {
  return createSelector(selectAllCommunicationCenterEntities, (entities) =>
    entities.filter((e) => e.useCrewPhoneNumber === useCrewPhoneNumber).valueSeq(),
  );
}

export function selectCommunicationCenterEntitiesByAlertFrequencyForGroundVehicles(
  alertFrequencyForGroundVehicles: number,
) {
  return createSelector(selectAllCommunicationCenterEntities, (entities) =>
    entities
      .filter((e) => e.alertFrequencyForGroundVehicles === alertFrequencyForGroundVehicles)
      .valueSeq(),
  );
}

export function selectCommunicationCenterEntitiesByDisplayContactTimeline(
  displayContactTimeline: boolean,
) {
  return createSelector(selectAllCommunicationCenterEntities, (entities) =>
    entities.filter((e) => e.displayContactTimeline === displayContactTimeline).valueSeq(),
  );
}

export function selectCommunicationCenterEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllCommunicationCenterEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
